/**
 * @linkhr/hr-domain/eos-calculation — Phase 08 Plan 08-13 (PAY-06).
 *
 * Zod boundary for the EOS (End-of-Service) calculation view + override
 * request body. Mirrors `components["schemas"]["EosCalculationView"]` from
 * the generated OpenAPI schema (CC-68 / be-062, 2026-04-28).
 *
 * Backend formula (Jordan Labour Law Art. 32):
 *   basicComponent       = serviceYears × monthsPerYear × basicSalary
 *   supplementalComponent = 0 (v1; reserved for v2 benefits accrual)
 *   calculatedAmount     = basicComponent + supplementalComponent
 *
 * The schema runs at the hook boundary via `safeEosCalculationParse` —
 * mirrors the `safePayslipParse` / `safeRunParse` pattern: returns null on
 * failure, emits a Sentry breadcrumb so we can monitor schema drift
 * post-CC-68 ship without breaking users.
 */

import { z } from "zod";

/**
 * Mirrors `components["schemas"]["EosOverrideView"]` — the latest override
 * record for this employee, if any.
 */
export const eosOverrideViewSchema = z.object({
  finalAmount: z.number().nonnegative(),
  reason: z.string(),
  /** ISO datetime (string per OpenAPI Format: date-time). */
  overriddenAt: z.string(),
  /**
   * Resolved actor who applied the override. The contract marks this
   * `EosOverriddenBy | null` AND optional (schema.d.ts EosOverrideView) — a
   * valid override can arrive with no resolved actor (system/service override,
   * or the actor row was since erased). Requiring it here made
   * `safeEosCalculationParse` return null on legitimate responses, degrading the
   * card to the misleading "no calculation" empty state. Consumers render a
   * fallback actor label when this is absent.
   */
  overriddenBy: z
    .object({
      employeeId: z.string(),
      name: z.string(),
    })
    .nullish(),
});
export type EosOverrideView = z.infer<typeof eosOverrideViewSchema>;

/**
 * Mirrors `components["schemas"]["EosCalculationView"]` (CC-68 ship shape).
 *
 * All numeric fields are `nonnegative` (Jordan EOS cannot be negative);
 * `currency` is locked to JOD; `override` is nullable when no override
 * has been recorded yet.
 */
export const eosCalculationViewSchema = z.object({
  employeeId: z.string(),
  serviceYears: z.number().int().nonnegative(),
  serviceMonths: z.number().int().min(0).max(12),
  /** Calendar-day pro-rated years (days/365), scale 4 — CC-227. Optional so
   *  pre-CC-227 fixtures still parse; consumers fall back to serviceYears. */
  proratedServiceYears: z.number().nonnegative().optional(),
  /** Jordan Art. 32 multiplier — typically 1.0 (one month/year). */
  monthsPerYear: z.number().positive(),
  /** Monthly basic pay in JOD. */
  basicSalary: z.number().nonnegative(),
  /** = serviceYears × monthsPerYear × basicSalary. */
  basicComponent: z.number().nonnegative(),
  /** Always 0 in v1; reserved for v2 benefits accrual. */
  supplementalComponent: z.number().nonnegative(),
  /** = basicComponent + supplementalComponent. */
  calculatedAmount: z.number().nonnegative(),
  /**
   * Contract type is plain `string` (schema.d.ts EosCalculationView.currency).
   * Locking to `z.literal("JOD")` hard-failed the whole parse on any other
   * value, sending a genuine calculation to the "no calculation" empty state.
   * We accept any string; the UI expects JOD but must not reject the money.
   */
  currency: z.string(),
  override: eosOverrideViewSchema.nullish(),
});

export type EosCalculationView = z.infer<typeof eosCalculationViewSchema>;

/**
 * Min reason length per CONTEXT.md D-14 — every override must include a
 * justification of at least 20 characters so the audit log is meaningful.
 */
export const EOS_OVERRIDE_REASON_MIN = 20;

/**
 * Mirrors `components["schemas"]["EosOverrideRequest"]` — body for
 * POST /api/v1/employees/{employeeId}/eos-calculation:override.
 */
export const eosOverrideRequestSchema = z.object({
  finalAmount: z.number().nonnegative(),
  reason: z
    .string()
    // i18n key (resolved + interpolated with `count` at the form render), so
    // Arabic admins don't see a hardcoded English validation string.
    .min(EOS_OVERRIDE_REASON_MIN, "payroll:admin.eos.reason_min_error"),
});

export type EosOverrideRequest = z.infer<typeof eosOverrideRequestSchema>;

/**
 * Sentry global accessor — installed by the app bootstrap. No-op in tests.
 */
type SentryShim = {
  addBreadcrumb?: (b: { message: string; level?: string; data?: unknown }) => void;
};

function captureDrift(issues: z.ZodIssue[]): void {
  if (typeof window === "undefined") return;
  const sentry = (window as unknown as { Sentry?: SentryShim }).Sentry;
  sentry?.addBreadcrumb?.({
    message: "eos-calculation-schema-mismatch",
    level: "warning",
    data: { issues: issues.slice(0, 5) },
  });
}

/**
 * Boundary parser — mirrors safePayslipParse pattern from payslip-line.ts.
 *
 * Returns the parsed view on success, or `null` when the backend response
 * does not match the expected shape (drift). On failure, emits a Sentry
 * breadcrumb and console.warn so production telemetry surfaces the drift
 * without blocking the user.
 */
export function safeEosCalculationParse(raw: unknown): EosCalculationView | null {
  const result = eosCalculationViewSchema.safeParse(raw);
  if (result.success) return result.data;
  captureDrift(result.error.issues);
  if (typeof console !== "undefined" && typeof console.warn === "function") {
    console.warn("[eos-calculation-shape-drift]", {
      issues: result.error.issues,
      raw,
    });
  }
  return null;
}
